import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { trackClientEvent } from 'analytics/analytics'
import useBedbankOfferSell from 'hooks/useBedbankOfferSell'
import BaseOfferPriceContent from './BaseOfferPriceContent'
import { getDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import config from 'constants/config'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import { getHotelOfferDisplayedValue } from 'lib/offer/getOfferDisplayPricing'

interface Props {
  duration?: number
  roomCount?: number
  offer: App.BedbankOffer
  alternativeCheckIn?: string
  alternativeCheckOut?: string
  alternativePrice?: number
  alternativeMemberPrice?: number
  alternativeValue?: number
}

const ALTERNATIVE_DATE_FORMAT = INTL_DAY_MONTH_NAME_SHORT

function DatedBedbankHotelPriceContent({
  duration,
  roomCount,
  offer,
  alternativeCheckIn,
  alternativeCheckOut,
  alternativePrice,
  alternativeMemberPrice,
  alternativeValue,
}: Props) {
  const intl = useIntl()
  const luxPlusLPPEnabled = useAppSelector(getIsLuxPlusLPPEnabled)
  const enableDurationSearch = !!config.LERE_ENABLE_CALENDAR_DURATION_FILTER

  useEffect(() => {
    if (!Number.isFinite(duration)) {
      trackClientEvent({
        action: `duration_not_finite::${duration}`,
        subject: 'BedbankHotelPriceContent',
        category: 'logging',
        type: 'operational',
      })
    }
  }, [duration])

  if (!enableDurationSearch) {
    return null
  }

  // Dated path: same LuxPlusPriceStack / roundedDollar display as Offer Page (no Math.ceil).
  const publicPrice = alternativePrice ?? 0
  const datedMemberPrice = luxPlusLPPEnabled ? (alternativeMemberPrice ?? 0) : 0
  const visiblePrice = publicPrice > 0 ? publicPrice : datedMemberPrice
  if (visiblePrice <= 0) {
    return null
  }

  const datedCaption = intl.formatMessage(
    {
      id: 'ZIUw_BWYEkPEO4eczXLP',
      defaultMessage: '{formattedAlternativeCheckIn} - {formattedAlternativeCheckOut} from',
    },
    {
      formattedAlternativeCheckIn: intlDateFormatter(alternativeCheckIn, ALTERNATIVE_DATE_FORMAT, intl.locale),
      formattedAlternativeCheckOut: intlDateFormatter(alternativeCheckOut, ALTERNATIVE_DATE_FORMAT, intl.locale),
    },
  )

  const {
    highestDiscountPercent,
    shouldShowValue,
    shouldShowDiscountPercent,
  } = getHotelOfferDisplayedValue(false, datedMemberPrice || publicPrice, alternativeValue ?? 0, true)

  return <BaseOfferPriceContent
    offer={offer}
    duration={duration || 0}
    roomCount={roomCount}
    captionText={datedCaption}
    checkIn={alternativeCheckIn}
    checkOut={alternativeCheckOut}
    memberPrice={datedMemberPrice}
    price={visiblePrice}
    value={alternativeValue ?? 0}
    discount={shouldShowDiscountPercent ? highestDiscountPercent : undefined}
    showValueDiscount={shouldShowValue}
  />
}

function ListBedbankHotelPriceContent({
  duration,
  offer,
}: Props) {
  const intl = useIntl()
  const enableDurationSearch = !!config.LERE_ENABLE_CALENDAR_DURATION_FILTER
  const sell = useBedbankOfferSell(offer, duration ?? 0, [])

  useEffect(() => {
    if (!Number.isFinite(duration)) {
      trackClientEvent({
        action: `duration_not_finite::${duration}`,
        subject: 'BedbankHotelPriceContent',
        category: 'logging',
        type: 'operational',
      })
    }
  }, [duration])

  if (!enableDurationSearch) {
    return null
  }

  const propertyFees = sell?.propertyFees ?? 0
  const hotelPrice = sell?.price ?? 0
  const hotelNights = sell?.los ?? 0
  const hotelValue = sell?.value ?? 0
  const hotelDiscount = sell?.discountPercentage ?? 0

  if (!hotelPrice || !hotelNights || !hotelValue) {
    return null
  }

  const totalPrices = getDisplayTotalPrice({
    hotelPrice: hotelPrice + propertyFees,
    hotelValue,
    hotelDiscount,
    rooms: [],
  })
  const nightsCaption = intl.formatMessage(
    {
      id: 'FTEKXrq-86oK79Lb2q2B',
      defaultMessage: '{count, plural, one {# night} other {# nights}} from',
    },
    { count: hotelNights },
  )

  return <BaseOfferPriceContent
    offer={offer}
    duration={hotelNights}
    captionText={nightsCaption}
    memberPrice={0}
    price={totalPrices.totals.price}
    value={totalPrices.totals.value}
    discount={totalPrices.original.discount}
    showValueDiscount={totalPrices.original.discount > 0}
  />
}

function BedbankHotelPriceContent(props: Props) {
  const hasExactDates = !!(props.alternativeCheckIn && props.alternativeCheckOut)
  if (hasExactDates) {
    return <DatedBedbankHotelPriceContent {...props} />
  }

  return <ListBedbankHotelPriceContent {...props} />
}

export default BedbankHotelPriceContent
